/** SEN-180 — the ONE seam for type metadata: keys, query options, invalidation.
 *
 *  A type switch used to cost three SEQUENTIAL waves of round trips:
 *
 *    1. metadata   types/{id} + attributes + views + quantities   <- gates 2
 *    2. query      records/query -> record ids                    <- gates 3
 *    3. values     records/values + chips
 *
 *  Requests WITHIN a wave are parallel, so a wave costs about one round trip
 *  however many requests it holds — the number of SEQUENTIAL waves is the whole
 *  cost model. Production HAR 2026-09-23 (jysk): the metadata wave alone was
 *  759–1,068 ms, typically 2–4x the values wave it gates. The server is not
 *  slow (`/view-kinds` is 9.5–14.4 ms measured inside the host); ~85 ms per
 *  request is irreducible browser->VPS network. So the only lever is to not
 *  make the requests at all.
 *
 *  Type metadata — the type row, its attributes, its views, its quantities —
 *  changes on a schema edit and effectively never otherwise. Every call site
 *  therefore reads it through the options factories below, which carry a long
 *  `staleTime` (no refetch on a switch) AND a long `gcTime` (the entry survives
 *  being unmounted while the user is on another type — the default 5 min gcTime
 *  is what made a revisit cold even when nothing had changed).
 *
 *  ## Why the options live here and not at each call site
 *
 *  The same `getAttributes` response was read under `['attributes', typeId]` at
 *  seven call sites and `['attributes', slug, id]` at three more — two cache
 *  entries for byte-identical data, of which only the first was ever
 *  invalidated. staleTime varied 30s/60s/300s/none for the same bytes. A
 *  constant repeated at ten call sites is the drift class; one factory is the
 *  fix. Adding a metadata reader = calling a factory, never re-deriving a key.
 *
 *  ## Liveness: what is deliberately traded
 *
 *  - THIS client edits the schema -> it knows, and MUST invalidate. That is the
 *    `invalidate*` half of this module, and it is the half that decides whether
 *    the caching is smart or brittle. Every metadata mutation call site goes
 *    through it.
 *  - ANOTHER client edits the schema -> this client keeps the old schema until
 *    something refetches. Today that self-heals within seconds only because
 *    everything is refetched constantly; that constant refetching is precisely
 *    the cost being removed. This is an ACCEPTED, STATED trade (one tenant,
 *    rare schema edits). `METADATA_STALE_TIME` is deliberately finite rather
 *    than `Infinity` so window-focus revalidation still self-heals a long-lived
 *    tab — and because a stale-but-cached entry costs NO latency, React Query
 *    serves the cached value synchronously and revalidates in the background,
 *    so the metadata wave stops being SEQUENTIAL even when it does refetch.
 */
import type { QueryClient } from '@tanstack/react-query'

import { monadClient } from './client'
import { getQuantities } from './reduce'
import { getViewKinds } from '../views/catalog'

/** Long enough that no realistic sequence of type switches refetches, short
 *  enough that a tab left open all day still revalidates on focus. */
export const METADATA_STALE_TIME = 10 * 60_000
/** The entry must OUTLIVE unmounting — a type switch unmounts the previous
 *  type's readers, and React Query's default 5-minute gcTime would evict the
 *  metadata a user returns to. This is the half that makes a revisit warm. */
export const METADATA_GC_TIME = 60 * 60_000

const shared = { staleTime: METADATA_STALE_TIME, gcTime: METADATA_GC_TIME } as const

// ---------------------------------------------------------------------------
// Keys — derived in exactly one place each.
// ---------------------------------------------------------------------------

/** NB: the per-type keys intentionally omit `slug`. The app is single-tenant
 *  per mount (`SLUG` is a module constant) and the keys predate this module at
 *  six call sites; adding `slug` here would split every existing entry. A
 *  tenant switch discards the client. */
export const typeKey = (typeId: string) => ['type', typeId] as const
export const attributesKey = (typeId: string) => ['attributes', typeId] as const
export const viewsKey = (typeId: string) => ['views', typeId] as const
export const typeListKey = (slug: string) => ['types', slug] as const
export const quantitiesKey = (slug: string, entityDef: string | undefined | null) =>
  ['quantities', slug, entityDef] as const
export const viewKindsKey = () => ['view-kinds'] as const

// ---------------------------------------------------------------------------
// Query options — spread into useQuery / fetchQuery / prefetchQuery.
// ---------------------------------------------------------------------------

export const typeQuery = (slug: string, typeId: string) => ({
  queryKey: typeKey(typeId),
  queryFn: () => monadClient.getType(slug, typeId),
  ...shared,
})

export const attributesQuery = (slug: string, typeId: string) => ({
  queryKey: attributesKey(typeId),
  queryFn: () => monadClient.getAttributes(slug, typeId),
  ...shared,
})

export const viewsQuery = (slug: string, typeId: string) => ({
  queryKey: viewsKey(typeId),
  queryFn: () => monadClient.getViews(slug, typeId),
  ...shared,
})

export const typeListQuery = (slug: string) => ({
  queryKey: typeListKey(slug),
  queryFn: () => monadClient.listTypes(slug),
  ...shared,
})

/** The type list with the reference types' last-write times (the reference-data directory). */
export const typeDirectoryQuery = (slug: string) => ({
  queryKey: [...typeListKey(slug), 'directory'] as const,
  queryFn: () => monadClient.listTypes(slug, { lastWrite: true }),
  ...shared,
})

export const quantitiesQuery = (slug: string, entityDef: string) => ({
  queryKey: quantitiesKey(slug, entityDef),
  queryFn: () => getQuantities(slug, entityDef),
  ...shared,
})

/** The view-kind catalog is global (not per type) and changes only on deploy. */
export const viewKindsQuery = () => ({
  queryKey: viewKindsKey(),
  queryFn: () => getViewKinds(),
  ...shared,
})

// ---------------------------------------------------------------------------
// Invalidation — the half that decides whether the caching is safe.
// ---------------------------------------------------------------------------

/** An ATTRIBUTE changed (created / edited / reordered / kind-changed).
 *
 *  Invalidates views as well, and that is NOT belt-and-braces: a view's columns
 *  are DERIVED FROM ATTRIBUTES ON READ. `monad/views_fabricator.py::
 *  resolve_view_columns` resolves an empty `column_paths` from the type's
 *  attribute metadata — "a new attribute appears in the default view without
 *  editing a view row" (SEN-1755 decision 1, ADR-0015 compute-on-read). So the
 *  cached `['views', typeId]` payload goes stale the moment an attribute is
 *  added, even though nothing wrote a view row. Under the old 60s staleTime
 *  that self-healed before anyone noticed; under METADATA_STALE_TIME it would
 *  not, and a newly created column would be missing from the table until
 *  reload. */
export function invalidateAttributes(qc: QueryClient, typeId: string): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: attributesKey(typeId) }),
    qc.invalidateQueries({ queryKey: viewsKey(typeId) }),
    // ...and QUANTITIES, for the same derive-on-read reason one level over:
    // `monad/quantities_catalog.py::list_quantities` builds the catalog partly
    // from `load_definitions_for_tenant`, which reads
    // `attributes WHERE type = 'calculated' AND config ? 'formula'`. Authoring
    // or editing a calculated attribute therefore changes what the type can
    // measure, with no quantities row written anywhere.
    qc.invalidateQueries({ queryKey: ['quantities'] }),
  ]).then(() => undefined)
}

/** TELEMETRY LANDED — a sync/load run carried new quantities.
 *
 *  Quantities are the one entry on this seam that is NOT purely schema-derived,
 *  and the distinction matters. The type row, its attributes and its views move
 *  only when a client edits the schema, so THIS client always knows. The
 *  quantities catalog also moves when the substrate underneath it moves:
 *  `monad/quantities_catalog.py::list_quantities` derives it from
 *  `carried_quantities()`, a `SELECT DISTINCT quantity FROM measurements_hourly`
 *  in ClickHouse, which gains rows the moment a sync run loads telemetry.
 *
 *  Before SEN-180 that was survivable by accident: App fetched quantities raw on
 *  every `entityDef` change and the dashboard readers inherited a 60s default.
 *  Under METADATA_STALE_TIME nothing would have rescued them — App's copy goes
 *  through `fetchQuery` and so has no observer, which means window-focus
 *  revalidation cannot reach it either. Without this, finishing a connection
 *  walk and opening "Add widget" would not offer the quantity that was just
 *  carried, for up to ten minutes.
 *
 *  Drops the whole `['quantities', ...]` prefix: the catalog is per tenant and
 *  per entity, and a run can carry a quantity for any entity it touched. */
export function invalidateQuantities(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({ queryKey: ['quantities'] })
}

/** A VIEW changed (columns reordered/resized/added, filters, sort, config). */
export function invalidateViews(qc: QueryClient, typeId: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: viewsKey(typeId) })
}

/** The TYPE ROW itself changed (name, icon, `display` — highlights, colour). */
export function invalidateType(qc: QueryClient, typeId: string): Promise<void> {
  return qc.invalidateQueries({ queryKey: typeKey(typeId) })
}

/** A type was CREATED or DELETED — the rail's list changed. Also drops the
 *  subtype catalog, which `SubtypeManager` derives from `listTypes`. */
export function invalidateTypeList(qc: QueryClient, slug: string): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: typeListKey(slug) }),
    qc.invalidateQueries({ queryKey: ['subtypes', slug] }),
    qc.invalidateQueries({ queryKey: ['subtype-meta', slug] }),
  ]).then(() => undefined)
}

/** Everything cached about ONE type. Used where a surface cannot say which of
 *  the three it just changed (the schema editor's post-mutation refetch). */
export function invalidateTypeMetadata(qc: QueryClient, typeId: string): Promise<void> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: typeKey(typeId) }),
    qc.invalidateQueries({ queryKey: attributesKey(typeId) }),
    qc.invalidateQueries({ queryKey: viewsKey(typeId) }),
  ]).then(() => undefined)
}

// ---------------------------------------------------------------------------
// Prefetch
// ---------------------------------------------------------------------------

/** Warm one type's metadata. `prefetchQuery` is a NO-OP when the entry is
 *  already fresh, so this is safe to call for every rail type repeatedly, and
 *  it never throws — a failed prefetch must be invisible. */
export function prefetchTypeMetadata(
  qc: QueryClient,
  slug: string,
  typeId: string,
): Promise<void> {
  return Promise.all([
    qc.prefetchQuery(typeQuery(slug, typeId)),
    qc.prefetchQuery(attributesQuery(slug, typeId)),
    qc.prefetchQuery(viewsQuery(slug, typeId)),
  ]).then(() => undefined)
}
